import { Link } from 'react-router-dom';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import type { ToolDashboardEntry } from '../../utils/dashboard';
import { TONE_BG, finalStateTone } from '../../utils/stateTone';

type Props = {
  entries: ToolDashboardEntry[];
};

/** Legend entries: the shared tool-state colours, then the other in-service states. */
const LEGEND = [
  { key: 'new', tone: 'success' },
  { key: 'active', tone: 'primary' },
  { key: 'worn', tone: 'warning' },
  { key: 'broken', tone: 'error' },
] as const;

export function ToolLivesPanel({ entries }: Props) {
  const { t } = useTranslation();

  const sorted = [...entries].sort((a, b) => b.totalPieces - a.totalPieces);
  const max = sorted[0]?.totalPieces ?? 1;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground mb-1">
        {LEGEND.map((item) => (
          <span key={item.key} className="flex items-center gap-1">
            <span className={cn('inline-block w-2 h-2 rounded-full', TONE_BG[item.tone])} />
            {t(`analytics.dashboard.lives.legend.${item.key}`)}
          </span>
        ))}
      </div>
      {sorted.length > 0 && (
        // Column captions, aligned with the rows below (pr-1 mirrors the scroll container).
        <div className="eyebrow flex items-center gap-2 pr-1">
          <span className="w-20 shrink-0">{t('analytics.dashboard.lives.columns.tool')}</span>
          <span className="flex-1">{t('analytics.dashboard.lives.columns.pieces')}</span>
          <span className="w-24 shrink-0 text-right">
            {t('analytics.dashboard.lives.columns.machine')}
          </span>
        </div>
      )}
      <div className="flex max-h-72 flex-col gap-2 overflow-y-auto pr-1">
        {sorted.map((entry) => {
          const pct = max > 0 ? (entry.totalPieces / max) * 100 : 0;
          const labelInside = pct > 25;
          const color = TONE_BG[finalStateTone(entry.finalState)];

          return (
            <div key={entry.toolId} className="flex items-center gap-2">
              <Link
                to={`/traceability/tools/${encodeURIComponent(entry.toolId)}`}
                className="w-20 shrink-0 text-xs font-mono text-primary hover:underline truncate"
              >
                {entry.toolId}
              </Link>
              <div className="relative flex-1 h-5 bg-muted rounded">
                <div
                  className={cn('h-full rounded transition-all', color)}
                  style={{ width: `${pct}%` }}
                />
                {labelInside ? (
                  <span className="absolute inset-0 flex items-center pl-2 text-xs font-medium text-primary-foreground">
                    {entry.totalPieces} {t('analytics.dashboard.lives.unit.pieces')}
                  </span>
                ) : (
                  <span
                    className="absolute top-0 flex h-full items-center pl-2 text-xs text-muted-foreground whitespace-nowrap"
                    style={{ left: `${pct}%` }}
                  >
                    {entry.totalPieces} {t('analytics.dashboard.lives.unit.pieces')}
                  </span>
                )}
              </div>
              <span className="w-24 shrink-0 text-xs text-muted-foreground text-right truncate">
                {entry.machine ?? ''}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
